// ============================================================================
// PacketContextPanel — Phase 1
//
// Renders inside GateView when activeFocusTarget?.packetId is set. Shows the
// packet's READY documents as neutral labels (no original filenames — PHI).
// Lets the GP deselect docs that should NOT promote into the new referral.
//
// Phase 1 explicitly does NOT auto-fill any clinical/patient/insurance field.
// ============================================================================

import React, { useEffect, useState } from 'react';
import { safeUserError } from '../../core/safeError.js';
import { FileText, Image as ImageIcon, AlertCircle, Loader2 } from 'lucide-react';
import {
  listPacketDocuments,
  PACKET_DOCUMENT_STATUS,
  resolveDocumentTypeLabel,
} from '../../core/store.packets.js';

const TYPE_TO_ICON = {
  PHOTO:   ImageIcon,
  IMAGING: ImageIcon,
};

const PacketContextPanel = ({ packetId, selectedIds, onChange, isEhrLaunch = false }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [docs, setDocs]       = useState([]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    listPacketDocuments(packetId)
      .then((res) => {
        if (cancelled) return;
        const readyDocs = (res?.documents || []).filter((d) => d.status === PACKET_DOCUMENT_STATUS.READY);
        setDocs(readyDocs);
        // Initialize selection to all READY docs the first time.
        if (selectedIds == null) {
          onChange(readyDocs.map((d) => d.documentId));
        }
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('[PacketContextPanel] listPacketDocuments failed:', err);
        setError(safeUserError(err));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [packetId]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (id) => {
    const next = (selectedIds || []).includes(id)
      ? (selectedIds || []).filter((x) => x !== id)
      : [...(selectedIds || []), id];
    onChange(next);
  };

  if (loading) {
    return (
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 flex items-center gap-2 text-sm text-slate-600">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading packet documents…
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700 flex items-start gap-2">
        <AlertCircle className="w-4 h-4 mt-0.5" />
        <div>
          <div className="font-semibold">Could not load packet documents</div>
          <div>{error}</div>
        </div>
      </div>
    );
  }

  if (docs.length === 0) {
    // EHR-launch packets are candidate-driven and carry no uploaded packet documents
    // by design — the GP can submit without attaching any. Pack-required attachments
    // are still enforced by the Gate, so make that distinction explicit.
    if (isEhrLaunch) {
      return (
        <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-4 text-sm text-indigo-900 space-y-1">
          <div className="font-semibold">EHR-launched packet — no uploaded documents</div>
          <div>
            This EHR-launched packet has no uploaded packet documents. You can submit
            without attaching documents, or add documents later if required by the Pack.
          </div>
          <div className="text-xs text-indigo-700">
            Submitting without packet documents does not skip Pack-required attachments —
            those are still enforced by the Gate.
          </div>
        </div>
      );
    }
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
        This packet has no READY documents. Nothing will be attached on submit.
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-4 space-y-3">
      <div className="flex items-center gap-2 text-sm font-semibold text-indigo-900">
        <FileText className="w-4 h-4" />
        Pre-Referral Packet — {docs.length} document{docs.length === 1 ? '' : 's'}
      </div>
      <div className="text-xs text-indigo-800">
        These documents will be attached to this referral on submit.
        Uncheck any document you do not want to attach.
      </div>
      <ul className="space-y-2">
        {docs.map((d, idx) => {
          const Icon = TYPE_TO_ICON[d.type] || FileText;
          // Phase 1.5: prefer the human classification label; fall back to the
          // neutral upload-type label (e.g. "PDF") when UNKNOWN/missing.
          const label = resolveDocumentTypeLabel(d);
          const checked = (selectedIds || []).includes(d.documentId);
          return (
            <li key={d.documentId} className="flex items-center gap-3 bg-white border border-indigo-100 rounded-md p-2 text-sm">
              <input
                type="checkbox"
                checked={checked}
                onChange={() => toggle(d.documentId)}
                className="h-4 w-4 text-indigo-600 border-slate-300 rounded"
              />
              <Icon className="w-4 h-4 text-slate-500" />
              <span className="font-medium text-slate-800">Document {idx + 1}</span>
              <span className="text-slate-500">· {label}</span>
              {d.sizeBytes != null && (
                <span className="ml-auto text-xs text-slate-400">{Math.round(d.sizeBytes / 1024)} KB</span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default PacketContextPanel;
